import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { goBack } from '../util/go_back.ts';
import { PlainLinkOutlined } from '../util/shared.tsx';

// The privacy policy lives in the website repo (src/content/privacy-policy.md). It is published at
// https://singyourpart.com/privacy for the store listings and, as a bare HTML fragment of the same
// text, at the URL below for this page, so a policy change is one website PR and needs no app
// release. The apps must not send people to the marketing site, which leads to purchase, so this
// page fetches the text and shows it here; the website's tests keep the policy free of links there
// (AGENTS.md, "Privacy policy").
export const privacyPolicyFragmentUrl = 'https://singyourpart.com/privacy-policy';
export const privacyPolicyContentTestId = 'privacy-policy-content';
export const privacyPolicyErrorTestId = 'privacy-policy-error';

export const PrivacyPage = () => {
  const navigate = useNavigate();
  const onBack = useCallback(() => {
    void goBack(navigate);
  }, [navigate]);
  const [html, setHtml] = useState<string>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(privacyPolicyFragmentUrl);
        if (!response.ok) {
          throw new Error(`privacy policy fetch failed: ${response.status}`);
        }
        const text = await response.text();
        if (!cancelled) {
          setHtml(text);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <PrivacyScrollable>
      <PrivacyContent>
        <h1>Our privacy policy</h1>
        {failed ? (
          <p data-testid={privacyPolicyErrorTestId}>
            We could not load the privacy policy. Check your connection and try again, or email{' '}
            <a href="mailto:business@crescendosw.com">business@crescendosw.com</a>.
          </p>
        ) : html === undefined ? (
          <p>Loading…</p>
        ) : (
          // our own site's text, fetched over HTTPS from singyourpart.com
          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
          <div data-testid={privacyPolicyContentTestId} dangerouslySetInnerHTML={{ __html: html }} />
        )}
      </PrivacyContent>
      <PlainLinkOutlined onClick={onBack}>Return to previous page</PlainLinkOutlined>
    </PrivacyScrollable>
  );
};

const PrivacyScrollable = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: scroll;
`;

const PrivacyContent = styled.div`
  text-align: left;
  padding: 5%;
  margin-bottom: 20px;
`;
